// src/components/assets/QRGenerator.tsx
import { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import type { Asset } from '../../types/asset';
import { getAssetDisplayName } from '../../utils/assetDisplayName';

interface QRGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  asset: Asset | null;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function QRGenerator({ isOpen, onClose, asset, showToast }: QRGeneratorProps) {
  const [codeType, setCodeType] = useState<'qr' | 'barcode'>('qr');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printSettings, setPrintSettings] = useState({
    width: 58,
    height: 24,
    orientation: 'landscape' as 'landscape' | 'portrait',
    scale: 100,
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barcodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (isOpen && asset) {
      generateCode();
    }
  }, [isOpen, asset, codeType]);

  const generateCode = async () => {
    if (!asset) return;

    const codeValue = asset.referenceNumber || asset.sku;

    if (codeType === 'qr' && canvasRef.current) {
      try {
        await QRCode.toCanvas(canvasRef.current, codeValue, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
        });
      } catch (err) {
        console.error('QR generation error:', err);
        showToast('Error generating QR code', 'error');
      }
    } else if (codeType === 'barcode' && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, codeValue, {
          format: 'CODE128',
          width: 2,
          height: 80,
          displayValue: true,
          fontSize: 14,
          margin: 10,
        });
      } catch (err) {
        console.error('Barcode generation error:', err);
        showToast('Error generating barcode', 'error');
      }
    }
  };

  const downloadLabel = () => {
    const element = codeType === 'qr' ? canvasRef.current : barcodeRef.current;
    if (!element || !asset) return;

    let dataUrl: string;

    if (codeType === 'qr' && canvasRef.current) {
      dataUrl = canvasRef.current.toDataURL('image/png');
    } else if (barcodeRef.current) {
      const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      dataUrl = URL.createObjectURL(svgBlob);
    } else {
      return;
    }

    const link = document.createElement('a');
    link.download = `${asset.referenceNumber || asset.sku}-${codeType}.png`;
    link.href = dataUrl;
    link.click();

    if (codeType === 'barcode') {
      URL.revokeObjectURL(dataUrl);
    }

    showToast('Label downloaded successfully', 'success');
  };

  const printLabel = () => {
    const element = codeType === 'qr' ? canvasRef.current : barcodeRef.current;
    if (!element || !asset) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow popups to print', 'error');
      return;
    }

    let content: string;
    if (codeType === 'qr' && canvasRef.current) {
      content = `<img src="${canvasRef.current.toDataURL('image/png')}" />`;
    } else if (barcodeRef.current) {
      content = new XMLSerializer().serializeToString(barcodeRef.current);
    } else {
      return;
    }

    // Escape user-controlled asset fields — this HTML is written to an
    // about:blank print window where the app CSP does not apply.
    const esc = (v: unknown) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Label - ${esc(asset.referenceNumber)}</title>
          <style>
            @page {
              size: ${printSettings.width}mm ${printSettings.height}mm;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              font-family: Arial, sans-serif;
            }
            .label-container {
              text-align: center;
              transform: scale(${printSettings.scale / 100});
            }
            .asset-name {
              font-size: 8pt;
              font-weight: bold;
              margin-bottom: 2mm;
              max-width: ${printSettings.width - 4}mm;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            img, svg {
              max-width: ${printSettings.width - 8}mm;
              max-height: ${printSettings.height - 12}mm;
            }
          </style>
        </head>
        <body>
          <div class="label-container">
            <div class="asset-name">${esc(getAssetDisplayName(asset))}</div>
            ${content}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  if (!asset) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Label"
      subtitle={asset.referenceNumber}
      size="md"
      gradient="from-purple-600 to-indigo-600"
    >
      <div className="space-y-4">
        {/* Code Type Toggle */}
        <div className="flex rounded-lg overflow-hidden border border-gray-300">
          <button
            onClick={() => setCodeType('qr')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition ${
              codeType === 'qr'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            QR Code
          </button>
          <button
            onClick={() => setCodeType('barcode')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition ${
              codeType === 'barcode'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            Barcode
          </button>
        </div>

        {/* Asset Info */}
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <p className="font-semibold text-gray-900">{getAssetDisplayName(asset)}</p>
          <p className="text-sm text-gray-600">{asset.referenceNumber || asset.sku}</p>
        </div>

        {/* Code Display */}
        <div className="flex justify-center p-4 bg-white border rounded-lg">
          {codeType === 'qr' ? (
            <canvas ref={canvasRef} />
          ) : (
            <svg ref={barcodeRef} />
          )}
        </div>

        <p className="text-center text-sm text-gray-600">
          {codeType === 'qr'
            ? 'Scan this QR code with any device'
            : 'Scan this barcode with a barcode scanner'}
        </p>

        {/* Print Settings */}
        {showPrintPreview && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-gray-900">Print Settings</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Width (mm)</label>
                <input
                  type="number"
                  value={printSettings.width}
                  onChange={(e) =>
                    setPrintSettings({ ...printSettings, width: parseInt(e.target.value) || 58 })
                  }
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Height (mm)</label>
                <input
                  type="number"
                  value={printSettings.height}
                  onChange={(e) =>
                    setPrintSettings({ ...printSettings, height: parseInt(e.target.value) || 24 })
                  }
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Scale (%)</label>
                <input
                  type="number"
                  value={printSettings.scale}
                  onChange={(e) =>
                    setPrintSettings({ ...printSettings, scale: parseInt(e.target.value) || 100 })
                  }
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={downloadLabel} icon="Download" variant="secondary">
            Download PNG
          </Button>
          <Button
            onClick={() => setShowPrintPreview(!showPrintPreview)}
            icon="Cog"
            variant="secondary"
          >
            {showPrintPreview ? 'Hide Settings' : 'Print Setup'}
          </Button>
        </div>

        {showPrintPreview && (
          <Button onClick={printLabel} icon="Printer" className="w-full">
            Print Label
          </Button>
        )}

        <Button variant="secondary" onClick={onClose} className="w-full">
          Close
        </Button>
      </div>
    </Modal>
  );
}
